// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import RichPresenceCard from '@app/features/presence/components/cards/RichPresenceCard';
import userActivityStore, {
	ACTIVITY_PRESETS,
	type ActivityData,
	type ActivityPreset,
	type ActivityType,
} from '@app/features/presence/state/UserActivity';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import {Input} from '@app/features/ui/components/form/FormInput';
import {Trans} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';
import styles from './ActivityModal.module.css';

const ACTIVITY_TYPE_OPTIONS: ReadonlyArray<{value: ActivityType; label: string}> = [
	{value: 'playing', label: 'Playing'},
	{value: 'listening', label: 'Listening to'},
	{value: 'streaming', label: 'Streaming'},
	{value: 'watching', label: 'Watching'},
	{value: 'competing', label: 'Competing in'},
];

export const ActivityModal: React.FC = observer(() => {
	const current = userActivityStore.currentActivity;
	const [type, setType] = useState<ActivityType>(current?.type ?? 'playing');
	const [name, setName] = useState<string>(current?.name ?? '');
	const [details, setDetails] = useState<string>(current?.details ?? '');
	const [state, setState] = useState<string>(current?.state ?? '');
	const [showTime, setShowTime] = useState<boolean>(current?.showTime ?? true);
	const [activePresetId, setActivePresetId] = useState<string | null>(() => {
		if (!current) return null;
		const match = ACTIVITY_PRESETS.find((p) => p.name.toLowerCase() === current.name.toLowerCase());
		return match?.id ?? null;
	});
	const [isSaving, setIsSaving] = useState(false);

	const handleSelectPreset = useCallback((preset: ActivityPreset) => {
		setActivePresetId(preset.id);
		setType(preset.type);
		setName(preset.name);
		if (preset.defaultDetails) setDetails(preset.defaultDetails);
		if (preset.defaultState) setState(preset.defaultState);
	}, []);

	const previewActivity = useMemo<ActivityData>(
		() => ({
			type,
			name: name.trim() || 'Game or App',
			details: details.trim() || undefined,
			state: state.trim() || undefined,
			startedAt: current?.startedAt ?? Date.now(),
			showTime,
		}),
		[type, name, details, state, showTime, current?.startedAt],
	);

	const handleSave = useCallback(async () => {
		const trimmedName = name.trim();
		if (!trimmedName) return;
		setIsSaving(true);
		try {
			await userActivityStore.setActivity({
				type,
				name: trimmedName,
				details: details.trim() || undefined,
				state: state.trim() || undefined,
				startedAt: current?.startedAt ?? Date.now(),
				showTime,
			});
			ModalCommands.pop();
		} finally {
			setIsSaving(false);
		}
	}, [type, name, details, state, showTime, current?.startedAt]);

	const handleClear = useCallback(async () => {
		setIsSaving(true);
		try {
			await userActivityStore.clearActivity();
			ModalCommands.pop();
		} finally {
			setIsSaving(false);
		}
	}, []);

	return (
		<Modal.Root size="medium" centered data-flx="presence.activity-modal.root">
			<Modal.Header title={<Trans>Set Activity & Rich Presence</Trans>} data-flx="presence.activity-modal.header" />
			<Modal.Content contentClassName={styles.content} data-flx="presence.activity-modal.content">
				<div className={styles.section}>
					<span className={styles.sectionTitle}>
						<Trans>Quick Presets</Trans>
					</span>
					<div className={styles.presetChips}>
						{ACTIVITY_PRESETS.map((preset) => (
							<button
								key={preset.id}
								type="button"
								className={clsx(styles.presetChip, activePresetId === preset.id && styles.presetChipActive)}
								onClick={() => handleSelectPreset(preset)}
							>
								{preset.name}
							</button>
						))}
					</div>
				</div>

				<div className={styles.section}>
					<Combobox
						label={<Trans>Activity Type</Trans>}
						value={type}
						options={ACTIVITY_TYPE_OPTIONS}
						onChange={(val) => setType(val as ActivityType)}
					/>
					<Input
						label={<Trans>Activity Name</Trans>}
						placeholder="e.g. Minecraft, Spotify, VS Code"
						value={name}
						onChange={(e) => {
							setName(e.target.value);
							setActivePresetId(null);
						}}
						required
					/>
					<Input
						label={<Trans>Details (optional)</Trans>}
						placeholder="e.g. Survival Mode, Competitive Match"
						value={details}
						onChange={(e) => setDetails(e.target.value)}
					/>
					<Input
						label={<Trans>State (optional)</Trans>}
						placeholder="e.g. Exploring Overworld, Score: 12 - 8"
						value={state}
						onChange={(e) => setState(e.target.value)}
					/>
					<label className={styles.checkboxRow}>
						<input
							type="checkbox"
							checked={showTime}
							onChange={(e) => setShowTime(e.target.checked)}
							className={styles.checkbox}
						/>
						<span>
							<Trans>Display elapsed time ticker</Trans>
						</span>
					</label>
				</div>

				<div className={styles.previewSection}>
					<span className={styles.sectionTitle}>
						<Trans>Live Preview</Trans>
					</span>
					<RichPresenceCard activity={previewActivity} />
				</div>
			</Modal.Content>
			<Modal.Footer className={styles.footer} data-flx="presence.activity-modal.footer">
				{current ? (
					<Button variant="danger" onClick={handleClear} disabled={isSaving}>
						<Trans>Clear Activity</Trans>
					</Button>
				) : (
					<div />
				)}
				<div className={styles.footerActions}>
					<Button variant="secondary" onClick={ModalCommands.pop} disabled={isSaving}>
						<Trans>Cancel</Trans>
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={!name.trim() || isSaving}
						submitting={isSaving}
					>
						<Trans>Save Activity</Trans>
					</Button>
				</div>
			</Modal.Footer>
		</Modal.Root>
	);
});

ActivityModal.displayName = 'ActivityModal';
export default ActivityModal;
